<template>
  <div class="mp-tencent-prediction-page">
    <div class="page-toolbar">
      <div>
        <h2>{{ pageTitle }}</h2>
        <span>{{ pageSubtitle }}</span>
      </div>
      <div class="toolbar-actions">
        <el-popover
          v-if="singleGameSnapshotVisible"
          placement="bottom-end"
          trigger="hover"
          width="280"
          popper-class="snapshot-status-popover"
          @show="loadSingleGameSnapshotStatus"
        >
          <div class="snapshot-status-panel" v-loading="singleGameSnapshotLoading">
            <div class="snapshot-status-title">{{ query.gameName }} 获取状态</div>
            <div class="snapshot-status-row">
              <span>状态</span>
              <el-tag size="mini" :type="snapshotStatusType(singleGameSnapshotStatus.status)">
                {{ snapshotStatusLabel(singleGameSnapshotStatus.status) }}
              </el-tag>
            </div>
            <div class="snapshot-status-row">
              <span>最近获取</span>
              <strong>{{ singleGameSnapshotStatus.updatedAt || '-' }}</strong>
            </div>
            <div class="snapshot-status-row">
              <span>最新注册日期</span>
              <strong>{{ formatDate(singleGameSnapshotStatus.latestRegDs) || '-' }}</strong>
            </div>
            <div class="snapshot-status-row">
              <span>重试次数</span>
              <strong>{{ singleGameSnapshotStatus.retryCount || 0 }}</strong>
            </div>
            <div class="snapshot-status-reason">
              <span>原因</span>
              <p>{{ snapshotStatusReason(singleGameSnapshotStatus) }}</p>
            </div>
          </div>
          <el-button
            slot="reference"
            size="mini"
            circle
            class="snapshot-status-button"
            :type="snapshotStatusType(singleGameSnapshotStatus.status)"
            icon="el-icon-info"
          ></el-button>
        </el-popover>
        <el-button
          size="mini"
          icon="el-icon-download"
          :disabled="loading || tableData.length === 0"
          @click="exportPredictionTable"
        >
          导出表格
        </el-button>
        <el-button size="mini" icon="el-icon-refresh" :loading="loading" @click="refreshPage">刷新</el-button>
      </div>
    </div>

    <section class="query-section">
      <div class="query-bar">
        <span class="field-label">项目名称:</span>
        <el-select
          v-model="query.gameName"
          size="mini"
          filterable
          clearable
          placeholder="游戏选择"
          style="width: 180px"
        >
          <el-option v-for="game in games" :key="game" :label="game" :value="game"></el-option>
        </el-select>

        <span class="field-label">日期:</span>
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          size="mini"
          value-format="yyyyMMdd"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          :picker-options="pickerOptions"
          style="width: 250px"
        ></el-date-picker>

        <el-select
          v-model="query.gameType"
          size="mini"
          filterable
          clearable
          placeholder="品类选择"
          style="width: 120px"
        >
          <el-option v-for="type in gameTypes" :key="type" :label="type" :value="type"></el-option>
        </el-select>

        <el-checkbox v-model="showAdRevenue">含广告金</el-checkbox>
        <el-button
          v-if="isVideoSource"
          size="mini"
          icon="el-icon-setting"
          @click="openVideoAdFundConfig"
        >
          广告金系数
        </el-button>

        <el-button size="mini" type="primary" icon="el-icon-search" :loading="loading" @click="queryData">查询</el-button>
        <el-button
          size="mini"
          icon="el-icon-refresh"
          :loading="snapshotRunLoading"
          :title="taskCalcDateTip('补发快照')"
          @click="submitSnapshotTask"
        >
          补发快照
        </el-button>
        <el-button
          size="mini"
          icon="el-icon-magic-stick"
          :loading="runLoading"
          :title="taskCalcDateTip('计算曲线分组')"
          @click="runCurvePrediction"
        >
          计算曲线分组
        </el-button>
        <span v-if="snapshotTaskId" class="inline-task-status">
          快照任务 {{ snapshotTaskId }}：
          <el-tag size="mini" :type="snapshotTaskTagType">{{ taskStatusText(snapshotTaskStatus) }}</el-tag>
          <span>{{ snapshotTaskMessage }}</span>
        </span>
        <span v-if="predictionTaskId" class="inline-task-status">
          曲线任务 {{ predictionTaskId }}：
          <el-tag size="mini" :type="predictionTaskTagType">{{ taskStatusText(predictionTaskStatus) }}</el-tag>
          <span>{{ predictionTaskMessage }}</span>
        </span>

        <el-checkbox-group v-model="checkedMetrics" :min="1" :max="2" class="metric-checks">
          <el-checkbox v-for="item in metricOptions" :key="item" :label="item">{{ item }}</el-checkbox>
        </el-checkbox-group>

        <el-popover placement="bottom" trigger="click" width="220">
          <div class="column-popover">
            <el-checkbox-group v-model="visibleColumns">
              <el-checkbox v-for="item in columnOptions" :key="item.value" :label="item.value">
                {{ item.label }}
              </el-checkbox>
            </el-checkbox-group>
          </div>
          <el-button slot="reference" size="mini" icon="el-icon-setting">列设置</el-button>
        </el-popover>
      </div>
    </section>

    <el-dialog
      title="视频号广告金系数"
      :visible.sync="videoAdFundDialogVisible"
      width="420px"
      append-to-body
    >
      <el-form
        v-loading="videoAdFundConfigLoading"
        label-width="120px"
        size="mini"
      >
        <el-form-item label="配置对象">
          <el-tag size="mini" :type="query.gameName ? 'success' : 'info'">
            {{ query.gameName || '全局默认' }}
          </el-tag>
          <span v-if="videoAdFundConfig.inheritedGlobal && query.gameName" class="video-ad-fund-inherit">
            当前沿用全局默认，保存后会覆盖该游戏
          </span>
        </el-form-item>
        <el-form-item label="1-30日系数">
          <el-input-number
            v-model="videoAdFundConfig.day1To30Rate"
            :min="0"
            :step="0.01"
            :precision="4"
            controls-position="right"
            style="width: 180px"
          ></el-input-number>
        </el-form-item>
        <el-form-item label="31-90日系数">
          <el-input-number
            v-model="videoAdFundConfig.day31To90Rate"
            :min="0"
            :step="0.01"
            :precision="4"
            controls-position="right"
            style="width: 180px"
          ></el-input-number>
        </el-form-item>
      </el-form>
      <div class="video-ad-fund-help">
        系数 0.20 表示对应区间广告金收入按接口收入的 20% 计算；31-90 日按分段累计，不覆盖前 30 日。修改后需要补发快照并重新计算曲线分组。
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button size="mini" @click="videoAdFundDialogVisible = false">取消</el-button>
        <el-button
          size="mini"
          type="primary"
          :loading="videoAdFundConfigSaving"
          @click="saveVideoAdFundConfig"
        >
          保存
        </el-button>
      </span>
    </el-dialog>

    <section v-if="false" class="status-section">
      <span class="status-title">预估状态</span>
      <el-tag size="mini" :type="statusTagType">{{ statusText }}</el-tag>
      <span v-if="predictionMode === 'self'" class="status-extra">
        Trace成功 {{ statusData.traceSuccessGames || 0 }} 个，Fit成功 {{ statusData.fitSuccessGames || 0 }} 个，预估 {{ statusData.predictionRows || 0 }} 条
      </span>
      <span v-else-if="predictionMode === 'ml'" class="status-extra">
        特征 {{ statusData.featureRows || 0 }} 条，模型 {{ statusData.modelRows || 0 }} 个，ML预估 {{ statusData.predictionRows || 0 }} 条
      </span>
      <span v-else class="status-extra">
        曲线预估 {{ statusData.predictionRows || 0 }} 条，回测 {{ statusData.backtestSampleRows || 0 }} 条
      </span>
      <span v-if="predictionTaskId" class="task-extra">
        预估任务 {{ predictionTaskId }}：
        <el-tag size="mini" :type="predictionTaskTagType">{{ taskStatusText(predictionTaskStatus) }}</el-tag>
        <span>{{ predictionTaskMessage }}</span>
      </span>
      <span v-if="backtestTaskId" class="task-extra">
        回测任务 {{ backtestTaskId }}：
        <el-tag size="mini" :type="backtestTaskTagType">{{ taskStatusText(backtestTaskStatus) }}</el-tag>
        <span>{{ backtestTaskMessage }}</span>
      </span>
    </section>

    <section v-if="false" class="backtest-section">
      <div class="backtest-header">
        <div>
          <span class="backtest-title">{{ backtestTitle }}</span>
          <span class="backtest-subtitle">{{ backtestSubtitle }}</span>
        </div>
        <div class="backtest-actions">
          <el-button size="mini" icon="el-icon-question" @click="backtestHelpVisible = true">指标说明</el-button>
          <el-select v-model="backtestPeriod" size="mini" clearable placeholder="周期" style="width: 95px" @change="loadBacktest">
            <el-option v-for="day in activeAccuracyDays" :key="day" :label="day + '日'" :value="day"></el-option>
          </el-select>
          <el-button size="mini" icon="el-icon-refresh" :loading="backtestLoading" @click="loadBacktest">查询回测</el-button>
        </div>
      </div>

      <el-table
        :data="visibleBacktestSummary"
        :height="backtestSummaryHeight"
        size="mini"
        border
        class="backtest-table"
        v-loading="backtestLoading"
        :header-cell-style="{background:'#f8fafc', color:'#323335'}"
      >
        <el-table-column prop="scopeType" label="维度" width="95">
          <template slot-scope="{row}">{{ scopeLabel(row.scopeType) }}</template>
        </el-table-column>
        <el-table-column prop="scopeValue" label="对象" min-width="150" show-overflow-tooltip></el-table-column>
        <el-table-column prop="periodDay" label="周期" width="70">
          <template slot-scope="{row}">{{ row.periodDay }}日</template>
        </el-table-column>
        <el-table-column prop="sampleCount" label="样本" width="70"></el-table-column>
        <el-table-column prop="wmape" label="WMAPE" width="95">
          <template slot-scope="{row}">{{ formatPercent(row.wmape) }}</template>
        </el-table-column>
        <el-table-column prop="biasRate" label="偏差率" width="95">
          <template slot-scope="{row}">
            <span :class="biasClass(row.biasRate)">{{ formatPercent(row.biasRate) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="mae" label="MAE" width="90">
          <template slot-scope="{row}">{{ formatPercent(row.mae) }}</template>
        </el-table-column>
        <el-table-column prop="rmse" label="RMSE" width="90">
          <template slot-scope="{row}">{{ formatPercent(row.rmse) }}</template>
        </el-table-column>
        <el-table-column prop="methodDistribution" label="方式分布" min-width="160" show-overflow-tooltip></el-table-column>
      </el-table>
      <div class="backtest-table-note">
        汇总表显示前 {{ visibleBacktestSummary.length }} / {{ backtestSummary.length }} 条，超过后在表格内部滚动查看。
      </div>

      <el-table
        :data="visibleBacktestSamples"
        :height="backtestSampleHeight"
        size="mini"
        border
        class="backtest-sample-table"
        :header-cell-style="{background:'#f8fafc', color:'#323335'}"
      >
        <el-table-column prop="regDs" label="日期" width="105">
          <template slot-scope="{row}">{{ formatDate(row.regDs) }}</template>
        </el-table-column>
        <el-table-column prop="gameName" label="游戏" min-width="140" show-overflow-tooltip></el-table-column>
        <el-table-column prop="gameType" label="品类" width="90" show-overflow-tooltip></el-table-column>
        <el-table-column prop="periodDay" label="目标" width="70">
          <template slot-scope="{row}">{{ row.periodDay }}日</template>
        </el-table-column>
        <el-table-column prop="baseDay" label="基准" width="70">
          <template slot-scope="{row}">{{ row.baseDay }}日</template>
        </el-table-column>
        <el-table-column prop="actualRoi" label="实际ROI" width="95">
          <template slot-scope="{row}">{{ formatPercent(row.actualRoi) }}</template>
        </el-table-column>
        <el-table-column prop="predictedRoi" label="预测ROI" width="95">
          <template slot-scope="{row}">{{ formatPercent(row.predictedRoi) }}</template>
        </el-table-column>
        <el-table-column prop="errorRate" label="误差率" width="95">
          <template slot-scope="{row}">{{ formatPercent(row.errorRate) }}</template>
        </el-table-column>
        <el-table-column prop="modelLevel" label="方式" width="105">
          <template slot-scope="{row}">
            <el-tag size="mini" :type="methodTagType(row.modelLevel)">{{ methodShortLabel(row.modelLevel) || row.modelLevel }}</el-tag>
          </template>
        </el-table-column>
      </el-table>
      <div class="backtest-table-note">
        明细表按加权误差从大到小显示前 {{ visibleBacktestSamples.length }} / {{ backtestSamples.length }} 条。
      </div>
    </section>

    <el-dialog
      v-if="false"
      title="腾讯预估倍率计算逻辑"
      :visible.sync="logicDialogVisible"
      width="720px"
      custom-class="logic-dialog"
    >
      <div class="logic-content">
        <div class="logic-block">
          <h3>数据来源</h3>
          <p>只使用注册来源为腾讯广告的数据，固定 out_id=33，不再拆分版位、创意和尺寸。</p>
          <p>实际 ROI 来自快照 Trace 数据；预估链路只使用自研历史倍率和训练分组，不再使用第三方 Fit 兜底。</p>
        </div>

        <div class="logic-block">
          <h3>实际值优先</h3>
          <p>某个注册日期已经有对应周期的 Trace ROI 时，页面直接展示真实值。</p>
          <p>只有未成熟、缺失或为 0 的周期才进入预估，并在页面中显示为蓝色。</p>
        </div>

        <div class="logic-block">
          <h3>自研预估</h3>
          <p>模型用“已知周期 K 到目标周期 T”的历史倍率预测，例如用 3 日 ROI 预测 7、15、30、60、90 日 ROI。</p>
          <p>倍率公式：历史倍率 = roiT / roiK；预测公式：预测 roiT = 当前 roiK × 历史加权倍率。</p>
        </div>

        <div class="logic-block">
          <h3>回测校准</h3>
          <p>执行回测后，系统会按游戏和周期计算“真实总 ROI / 预测总 ROI”的校准系数，下一次生成预估时只修正预估周期，已出的真实 Trace ROI 不会被改动。</p>
          <p>校准会按 WMAPE 和偏差率分成低、中、高风险，风险越高允许的修正幅度越大；页面中的校准风险标签可以判断该游戏近期预估是否稳定。</p>
        </div>

        <div class="logic-block">
          <h3>模型兜底顺序</h3>
          <p>优先使用同游戏历史样本；样本不足时使用同品类样本；再不足时使用腾讯广告全局样本。</p>
          <p>每个周期至少需要 10 条有效样本，样本按消耗和近期程度加权，并去掉极端倍率，减少异常数据影响。</p>
        </div>

        <div class="logic-block">
          <h3>曲线分组模式</h3>
          <p>曲线分组会先用首日 ROI、3/7/15 日 ROI、早期倍率、消耗、注册数和 CPA 做分组，15 日前优先用已有 ROI 点做对数拟合补齐。</p>
          <p>早期可用点数不足时保持空值，不用同分组趋势硬补；15 日后再按 15→30→45→60→75→90 的链式趋势补齐缺失周期。</p>
          <p>趋势优先级为同游戏同分组、同品类同分组、全局同分组；低首日高倍率组样本不足时可回退到混合模式组，高首日低倍率组会尝试对数拟合。</p>
        </div>

        <div class="logic-block">
          <h3>展示口径</h3>
          <p>含广告金使用 roi_all，不含广告金使用 roi_monetize。</p>
          <p>倍率 = 当前周期 ROI / 1 日 ROI；预估利润 = 消耗 × 预估 ROI - 消耗。</p>
          <p>预估 ROI 不允许低于前一个已知周期，避免累计 ROI 倒退；0 或空值统一显示为 -。</p>
          <p>标签按首日 ROI 和最长成熟周期倍率分位数划分，未成熟样本不会把缺失倍率当作 0：高首日ROI-低倍率组、低首日ROI-高倍率组、混合模式组、样本不足组。</p>
        </div>
      </div>
    </el-dialog>

    <el-dialog
      v-if="false"
      title="回测指标说明"
      :visible.sync="backtestHelpVisible"
      width="680px"
      custom-class="logic-dialog"
    >
      <div class="logic-content">
        <div class="logic-block">
          <h3>回测口径</h3>
          <p>回测只使用已经成熟的真实 Trace ROI 做校验。比如校验 30 日 ROI 时，只拿已经有真实 30 日 ROI 的注册日期做样本。</p>
          <p>模型预测时会模拟当时只知道较短周期 ROI 的场景，再和后续成熟的真实 ROI 对比，避免直接看未来。</p>
        </div>
        <div class="logic-block">
          <h3>汇总表</h3>
          <p>WMAPE 是按消耗加权后的整体误差，越低越好，适合判断实际投放金额影响下准不准。</p>
          <p>偏差率大于 0 表示整体预估偏高，小于 0 表示整体预估偏低；MAE 是平均绝对误差，RMSE 会更重视大误差样本。</p>
          <p>方式分布表示这些回测样本分别用了同游戏分组、同游戏整体、同品类、全局等层级，能看出模型主要依赖哪种层级。</p>
        </div>
        <div class="logic-block">
          <h3>明细表</h3>
          <p>明细表按加权误差从大到小排列，优先暴露“消耗高且偏差大”的问题样本。</p>
          <p>基准表示预测时可用的最长真实周期，目标表示本次校验的 ROI 周期。</p>
        </div>
      </div>
    </el-dialog>

    <section class="table-section">
      <el-table
        :key="tableRenderKey"
        ref="predictionTable"
        :data="displayTableData"
        :height="tableHeight"
        size="mini"
        highlight-current-row
        v-loading="loading"
        class="prediction-table"
        :header-cell-style="{background:'#f8fafc', color:'#323335'}"
        :row-class-name="tableRowClassName"
        @sort-change="handleSortChange"
      >
        <el-table-column prop="regDs" label="日期" width="110" fixed sortable="custom">
          <template slot-scope="{row}">{{ formatDate(row.regDs) }}</template>
        </el-table-column>

        <el-table-column
          prop="gameType"
          label="品类"
          width="90"
          fixed
          v-if="visibleColumns.includes('gameType')"
          show-overflow-tooltip
        ></el-table-column>

        <el-table-column
          prop="gameName"
          label="游戏名称"
          width="150"
          fixed
          v-if="visibleColumns.includes('gameName')"
          show-overflow-tooltip
        ></el-table-column>

        <el-table-column
          prop="adCost"
          label="消耗"
          width="105"
          sortable="custom"
          v-if="visibleColumns.includes('adCost')"
        >
          <template slot-scope="{row}">{{ formatMoney(row.adCost) }}</template>
        </el-table-column>

        <el-table-column
          prop="estimatedProfit"
          label="预估利润"
          width="105"
          sortable="custom"
          v-if="visibleColumns.includes('estimatedProfit')"
        >
          <template slot-scope="{row}">
            <span :class="profitClass(row.estimatedProfit)">{{ formatMoney(row.estimatedProfit) }}</span>
          </template>
        </el-table-column>

        <el-table-column
          prop="bestRoi"
          label="预估ROI|倍率"
          width="130"
          v-if="visibleColumns.includes('roiInfo')"
        >
          <template slot-scope="{row}">
            <span class="roi-inline">
              <span :class="roiValueClass(row.bestEstimated, row.bestMethod)" :title="cellTitle(row, row.bestPeriod)">
                {{ formatPercent(row.bestRoi) }}
              </span>
              <span v-if="hasValue(row.bestRoi) || hasValue(row.bestRate)"> | </span>
              <span :class="rateValueClass(row.bestEstimated, row.bestMethod)" :title="cellTitle(row, row.bestPeriod)">
                {{ formatRate(row.bestRate) }}
              </span>
            </span>
          </template>
        </el-table-column>

        <el-table-column
          prop="predictedPeriod"
          label="预估周期"
          width="85"
          v-if="visibleColumns.includes('predictedPeriod')"
        >
          <template slot-scope="{row}">{{ row.bestPeriod ? row.bestPeriod : '-' }}</template>
        </el-table-column>

        <el-table-column
          prop="predictionInterval"
          label="预测区间"
          width="145"
          v-if="visibleColumns.includes('predictionInterval')"
        >
          <template slot-scope="{row}">
            <span>{{ formatPredictionInterval(row) }}</span>
          </template>
        </el-table-column>

        <template v-for="day in displayRoiDays">
          <el-table-column
            :key="day"
            v-if="visibleColumns.includes('roiColumns')"
            :prop="'roi' + day"
            :label="`${day}日ROI`"
            width="145"
          >
            <template slot-scope="{row}">
              <span class="roi-inline">
                <span
                  v-if="checkedMetrics.includes('ROI')"
                  :class="roiValueClass(row[`roi${day}Estimated`], row[`roi${day}Method`])"
                  :title="cellTitle(row, day)"
                >
                  {{ formatPercent(row[`roi${day}`]) }}
                </span>
                <span v-if="checkedMetrics.includes('ROI') && checkedMetrics.includes('倍率')"> | </span>
                <span
                  v-if="checkedMetrics.includes('倍率')"
                  :class="rateValueClass(row[`roi${day}Estimated`], row[`roi${day}Method`])"
                  :title="cellTitle(row, day)"
                >
                  {{ formatRate(row[`rate${day}`]) }}
                </span>
              </span>
            </template>
          </el-table-column>
        </template>

        <el-table-column
          prop="groupLabel"
          label="标签"
          width="155"
          v-if="visibleColumns.includes('groupLabel')"
        >
          <template slot-scope="{row}">
            <el-tag size="mini" :type="groupTagType(displayGroupLabel(row))">
              {{ displayGroupLabel(row) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column
          prop="riskLevel"
          label="校准风险"
          width="95"
          v-if="false"
        >
          <template slot-scope="{row}">
            <el-tag v-if="row.riskLevel" size="mini" :type="riskTagType(row.riskLevel)" :title="calibrationSummary(row)">
              {{ riskLabel(row.riskLevel) }}
            </el-tag>
            <span v-else>-</span>
          </template>
        </el-table-column>

        <el-table-column
          prop="modelLevel"
          label="模型"
          width="180"
          v-if="false"
        >
          <template slot-scope="{row}">
            <span v-if="methodTags(row).length === 0">真实</span>
            <el-tag
              v-for="tag in methodTags(row)"
              :key="tag.method"
              size="mini"
              :type="methodTagType(tag.method)"
              class="method-tag"
            >
              {{ tag.label }}
            </el-tag>
          </template>
        </el-table-column>
      </el-table>

      <div class="table-count">共 {{ tableData.length }} 条记录</div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'MpTencentPrediction',
  data() {
    return {
      query: {
        gameName: '',
        gameType: '',
        calcDate: ''
      },
      dateRange: [],
      showAdRevenue: true,
      videoAdFundDialogVisible: false,
      videoAdFundConfigLoading: false,
      videoAdFundConfigSaving: false,
      videoAdFundConfig: {
        gameName: '',
        effectiveGameName: '*',
        inheritedGlobal: false,
        day1To30Rate: 0,
        day31To90Rate: 0,
        updatedAt: ''
      },
      predictionMode: 'curve',
      games: [],
      gameTypes: [],
      tableData: [],
      backtestSummary: [],
      backtestSamples: [],
      statusData: {},
      singleGameSnapshotStatus: {},
      loading: false,
      statusLoading: false,
      singleGameSnapshotLoading: false,
      runLoading: false,
      snapshotRunLoading: false,
      backtestLoading: false,
      snapshotTaskId: null,
      snapshotTaskStatus: '',
      snapshotTaskMessage: '',
      snapshotTaskTimer: null,
      predictionTaskId: null,
      predictionTaskStatus: '',
      predictionTaskMessage: '',
      predictionTaskTimer: null,
      backtestTaskId: null,
      backtestTaskStatus: '',
      backtestTaskMessage: '',
      backtestTaskTimer: null,
      logicDialogVisible: false,
      backtestHelpVisible: false,
      tableRenderKey: 0,
      sortState: null,
      roiDays: [1, 2, 3, 4, 5, 6, 7, 15, 30, 45, 60, 75, 90],
      curveRoiDays: [1, 2, 3, 4, 5, 6, 7, 15, 30, 45, 60, 75, 90],
      accuracyDays: [7, 15, 30, 60, 90],
      mlAccuracyDays: [30, 45, 60, 75, 90],
      curveAccuracyDays: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 30, 45, 60, 75, 90],
      backtestPeriod: '',
      backtestSummaryLimit: 80,
      backtestSampleLimit: 120,
      metricOptions: ['ROI', '倍率'],
      checkedMetrics: ['ROI', '倍率'],
      visibleColumns: ['gameType', 'gameName', 'adCost', 'estimatedProfit', 'roiInfo', 'predictedPeriod', 'predictionInterval', 'roiColumns', 'groupLabel'],
      columnOptions: [
        { value: 'gameType', label: '品类' },
        { value: 'gameName', label: '游戏名称' },
        { value: 'adCost', label: '消耗' },
        { value: 'estimatedProfit', label: '预估利润' },
        { value: 'roiInfo', label: '预估ROI|倍率' },
        { value: 'predictedPeriod', label: '预估周期' },
        { value: 'predictionInterval', label: '预测区间' },
        { value: 'roiColumns', label: 'ROI明细列' },
        { value: 'groupLabel', label: '标签' }
      ],
      pickerOptions: {
        shortcuts: [
          {
            text: '最近一周',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: '最近一个月',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: '最近三个月',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 90)
              picker.$emit('pick', [start, end])
            }
          }
        ],
        disabledDate(time) {
          return time.getTime() > Date.now()
        }
      }
    }
  },
  watch: {
    visibleColumns() {
      this.refreshTableLayout()
    },
    checkedMetrics() {
      this.refreshTableLayout()
    },
    showAdRevenue() {
      this.queryData()
    },
    'query.gameName'() {
      this.loadSingleGameSnapshotStatus()
    },
    '$route.name'() {
      this.singleGameSnapshotStatus = {}
      this.loadOptions()
      this.queryData()
    }
  },
  computed: {
    tableHeight() {
      return 'calc(100vh - 150px)'
    },
    displayRoiDays() {
      return this.predictionMode === 'curve' ? this.curveRoiDays : this.roiDays
    },
    displayTableData() {
      if (!Array.isArray(this.tableData) || this.tableData.length === 0) {
        return []
      }
      return [this.buildSummaryRow(this.tableData), ...this.tableData]
    },
    singleGameSnapshotVisible() {
      return !!(this.query && this.query.gameName)
    },
    backtestSummaryHeight() {
      return 220
    },
    backtestSampleHeight() {
      return 260
    },
    visibleBacktestSummary() {
      return (this.backtestSummary || []).slice(0, this.backtestSummaryLimit)
    },
    visibleBacktestSamples() {
      return (this.backtestSamples || []).slice(0, this.backtestSampleLimit)
    },
    activeAccuracyDays() {
      if (this.predictionMode === 'ml') {
        return this.mlAccuracyDays
      }
      if (this.predictionMode === 'curve') {
        return this.curveAccuracyDays
      }
      return this.accuracyDays
    },
    backtestTitle() {
      if (this.predictionMode === 'ml') {
        return 'ML回测体系'
      }
      if (this.predictionMode === 'curve') {
        return '曲线分组回测'
      }
      return '回测体系'
    },
    backtestSubtitle() {
      if (this.predictionMode === 'ml') {
        return '按验证集真实 ROI 校验机器学习模型'
      }
      if (this.predictionMode === 'curve') {
        return '按成熟真实 ROI 校验曲线分组模型'
      }
      return '按成熟真实 ROI 校验当前自研模型'
    },
    runButtonText() {
      if (this.predictionMode === 'ml') {
        return '生成ML预估'
      }
      if (this.predictionMode === 'curve') {
        return '生成曲线预估'
      }
      return '生成预估'
    },
    predictionTaskTagType() {
      return this.taskTagType(this.predictionTaskStatus)
    },
    backtestTaskTagType() {
      return this.taskTagType(this.backtestTaskStatus)
    },
    snapshotTaskTagType() {
      return this.taskTagType(this.snapshotTaskStatus)
    },
    modeSubtitle() {
      if (this.predictionMode === 'ml') {
        return '机器学习'
      }
      if (this.predictionMode === 'curve') {
        return '曲线分组'
      }
      return '自研预估'
    },
    statusText() {
      if (this.predictionMode === 'ml') {
        if ((this.statusData.predictionRows || 0) > 0) {
          return 'ML已生成'
        }
        if ((this.statusData.featureRows || 0) > 0) {
          return '特征已生成'
        }
        return 'ML未生成'
      }
      if (this.predictionMode === 'curve') {
        if ((this.statusData.predictionRows || 0) > 0) {
          return '曲线已生成'
        }
        if (this.statusData.snapshotReady) {
          return '快照已就绪'
        }
        return '曲线未生成'
      }
      if ((this.statusData.predictionRows || 0) > 0) {
        return '已生成'
      }
      if (this.statusData.snapshotReady) {
        return '快照已就绪'
      }
      return '快照未完成'
    },
    statusTagType() {
      if (this.statusText === 'ML已生成') {
        return 'success'
      }
      if (this.statusText === '特征已生成') {
        return 'warning'
      }
      if (this.statusText === 'ML未生成') {
        return 'info'
      }
      if (this.statusText === '曲线已生成') {
        return 'success'
      }
      if (this.statusText === '曲线未生成') {
        return 'info'
      }
      if (this.statusText === '已生成') {
        return 'success'
      }
      if (this.statusText === '快照已就绪') {
        return 'warning'
      }
      return 'info'
    },
    sourceOutId() {
      const meta = this.$route && this.$route.meta ? this.$route.meta : {}
      return String(meta.outId || '33') === '62' ? '62' : '33'
    },
    isVideoSource() {
      return this.sourceOutId === '62'
    },
    pageTitle() {
      return this.isVideoSource ? '视频号预估倍率' : '腾讯预估倍率'
    },
    pageSubtitle() {
      return (this.isVideoSource ? '视频号' : '腾讯广告') + ' · ' + this.modeSubtitle
    }
  },
  mounted() {
    if (!this.dateRange || this.dateRange.length === 0) {
      this.dateRange = this.defaultDateRange(this.defaultCalcDate())
    }
    this.loadOptions()
    this.queryData()
  },
  beforeDestroy() {
    this.stopSnapshotTaskPolling()
    this.stopPredictionTaskPolling()
    this.stopBacktestTaskPolling()
  },
  methods: {
    async refreshPage() {
      await this.loadOptions()
      await this.queryData()
    },
    exportPredictionTable() {
      if (!Array.isArray(this.tableData) || this.tableData.length === 0) {
        this.$message.warning('暂无可导出的数据')
        return
      }
      const XLSX = this.$xlsx
      if (!XLSX || !XLSX.utils || !XLSX.writeFile) {
        this.$message.error('导出组件未加载')
        return
      }
      const columns = this.exportPredictionColumns()
      const rows = this.displayTableData.map(row => {
        const item = {}
        columns.forEach(column => {
          item[column.label] = column.value(row)
        })
        return item
      })
      const headers = columns.map(column => column.label)
      const worksheet = XLSX.utils.json_to_sheet(rows, { header: headers })
      worksheet['!cols'] = columns.map(column => ({ wch: column.width || 14 }))
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, '预估倍率')
      this.appendPredictionMetricSheet(XLSX, workbook, 'ROI', 'roi')
      this.appendPredictionMetricSheet(XLSX, workbook, '倍率', 'rate')
      XLSX.writeFile(workbook, this.exportPredictionFileName())
      this.$message.success('导出成功')
    },
    appendPredictionMetricSheet(XLSX, workbook, sheetName, metricType) {
      const columns = this.exportPredictionMetricColumns(metricType)
      const rows = this.displayTableData.map(row => {
        const item = {}
        columns.forEach(column => {
          item[column.label] = column.value(row)
        })
        return item
      })
      const headers = columns.map(column => column.label)
      const worksheet = XLSX.utils.json_to_sheet(rows, { header: headers })
      worksheet['!cols'] = columns.map(column => ({ wch: column.width || 14 }))
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)
    },
    exportPredictionMetricColumns(metricType) {
      const isRate = metricType === 'rate'
      const columns = [
        { label: '日期', width: 12, value: row => this.formatDate(row.regDs) }
      ]
      if (this.visibleColumns.includes('gameType')) {
        columns.push({ label: '品类', width: 12, value: row => row.gameType || '-' })
      }
      if (this.visibleColumns.includes('gameName')) {
        columns.push({ label: '游戏名称', width: 24, value: row => row.gameName || '-' })
      }
      if (this.visibleColumns.includes('adCost')) {
        columns.push({ label: '消耗', width: 14, value: row => this.formatMoney(row.adCost) })
      }
      if (this.visibleColumns.includes('roiInfo')) {
        columns.push({
          label: isRate ? '预估倍率' : '预估ROI',
          width: 14,
          value: row => this.formatExportMetricValue(isRate ? row.bestRate : row.bestRoi, metricType)
        })
      }
      if (this.visibleColumns.includes('predictedPeriod')) {
        columns.push({ label: '预估周期', width: 12, value: row => row.bestPeriod ? row.bestPeriod : '-' })
      }
      if (this.visibleColumns.includes('predictionInterval')) {
        columns.push({ label: '预测区间', width: 20, value: row => this.formatPredictionInterval(row) })
      }
      if (this.visibleColumns.includes('roiColumns')) {
        this.displayRoiDays.forEach(day => {
          columns.push({
            label: day + (isRate ? '日倍率' : '日ROI'),
            width: 14,
            value: row => this.formatExportMetricValue(row[(isRate ? 'rate' : 'roi') + day], metricType)
          })
        })
      }
      if (this.visibleColumns.includes('groupLabel')) {
        columns.push({ label: '标签', width: 18, value: row => this.displayGroupLabel(row) })
      }
      return columns
    },
    formatExportMetricValue(value, metricType) {
      return metricType === 'rate' ? this.formatRate(value) : this.formatPercent(value)
    },
    exportPredictionColumns() {
      const columns = [
        { label: '日期', width: 12, value: row => this.formatDate(row.regDs) }
      ]
      if (this.visibleColumns.includes('gameType')) {
        columns.push({ label: '品类', width: 12, value: row => row.gameType || '-' })
      }
      if (this.visibleColumns.includes('gameName')) {
        columns.push({ label: '游戏名称', width: 24, value: row => row.gameName || '-' })
      }
      if (this.visibleColumns.includes('adCost')) {
        columns.push({ label: '消耗', width: 14, value: row => this.formatMoney(row.adCost) })
      }
      if (this.visibleColumns.includes('estimatedProfit')) {
        columns.push({ label: '预估利润', width: 14, value: row => this.formatMoney(row.estimatedProfit) })
      }
      if (this.visibleColumns.includes('roiInfo')) {
        columns.push({
          label: '预估ROI|倍率',
          width: 18,
          value: row => this.formatExportMetricCell(row.bestRoi, row.bestRate, true, true)
        })
      }
      if (this.visibleColumns.includes('predictedPeriod')) {
        columns.push({ label: '预估周期', width: 12, value: row => row.bestPeriod ? row.bestPeriod : '-' })
      }
      if (this.visibleColumns.includes('predictionInterval')) {
        columns.push({ label: '预测区间', width: 20, value: row => this.formatPredictionInterval(row) })
      }
      if (this.visibleColumns.includes('roiColumns')) {
        const includeRoi = this.checkedMetrics.includes('ROI')
        const includeRate = this.checkedMetrics.includes('倍率')
        this.displayRoiDays.forEach(day => {
          columns.push({
            label: this.exportRoiColumnLabel(day, includeRoi, includeRate),
            width: 16,
            value: row => this.formatExportMetricCell(row[`roi${day}`], row[`rate${day}`], includeRoi, includeRate)
          })
        })
      }
      if (this.visibleColumns.includes('groupLabel')) {
        columns.push({ label: '标签', width: 18, value: row => this.displayGroupLabel(row) })
      }
      return columns
    },
    exportRoiColumnLabel(day, includeRoi, includeRate) {
      if (includeRoi && includeRate) {
        return day + '日ROI|倍率'
      }
      if (includeRate) {
        return day + '日倍率'
      }
      return day + '日ROI'
    },
    formatExportMetricCell(roi, rate, includeRoi, includeRate) {
      const parts = []
      if (includeRoi) {
        parts.push(this.formatPercent(roi))
      }
      if (includeRate) {
        parts.push(this.formatRate(rate))
      }
      const validParts = parts.filter(item => item !== '-')
      return validParts.length > 0 ? parts.join(' | ') : '-'
    },
    exportPredictionFileName() {
      const start = this.dateRange && this.dateRange.length > 0 ? this.formatDate(this.dateRange[0]) : ''
      const end = this.dateRange && this.dateRange.length > 1 ? this.formatDate(this.dateRange[1]) : ''
      const dateText = start && end ? start + '_' + end : this.formatDateValue(new Date())
      const gameText = this.query.gameName ? '_' + this.safeFileName(this.query.gameName) : ''
      return this.safeFileName(this.pageTitle) + gameText + '_' + dateText + '.xlsx'
    },
    safeFileName(value) {
      return String(value || '').replace(/[\\/:*?"<>|]/g, '_')
    },
    async loadOptions() {
      await Promise.all([this.loadGames(), this.loadGameTypes()])
    },
    async loadGames() {
      try {
        const response = await this.$axios.get(this.optionsEndpoint('/games'))
        this.games = response.data.data || []
      } catch (error) {
        this.games = []
      }
    },
    async loadGameTypes() {
      try {
        const response = await this.$axios.get(this.optionsEndpoint('/gameTypes'))
        this.gameTypes = response.data.data || []
      } catch (error) {
        this.gameTypes = []
      }
    },
    optionsEndpoint(action) {
      return (this.isVideoSource ? '/mpVideoCurvePrediction' : '/mpTencentCurvePrediction') + action
    },
    predictionEndpoint(action) {
      return (this.isVideoSource ? '/mpVideoCurvePrediction' : '/mpTencentCurvePrediction') + action
    },
    async openVideoAdFundConfig() {
      this.videoAdFundDialogVisible = true
      await this.loadVideoAdFundConfig()
    },
    async loadVideoAdFundConfig() {
      if (!this.isVideoSource) {
        return
      }
      this.videoAdFundConfigLoading = true
      try {
        const response = await this.$axios.get(this.predictionEndpoint('/adFundConfig'), {
          params: {
            gameName: this.query.gameName || ''
          }
        })
        if (response.data.success) {
          const data = response.data.data || {}
          this.videoAdFundConfig = {
            gameName: data.gameName || '',
            effectiveGameName: data.effectiveGameName || '*',
            inheritedGlobal: !!data.inheritedGlobal,
            day1To30Rate: Number(data.day1To30Rate || 0),
            day31To90Rate: Number(data.day31To90Rate || 0),
            updatedAt: data.updatedAt || ''
          }
        } else {
          this.$message.error(response.data.msg || '获取广告金系数失败')
        }
      } catch (error) {
        this.$message.error('获取广告金系数失败')
      } finally {
        this.videoAdFundConfigLoading = false
      }
    },
    async saveVideoAdFundConfig() {
      if (!this.isVideoSource) {
        return
      }
      this.videoAdFundConfigSaving = true
      try {
        const response = await this.$axios.post(this.predictionEndpoint('/adFundConfig'), {
          gameName: this.query.gameName || '',
          day1To30Rate: this.videoAdFundConfig.day1To30Rate || 0,
          day31To90Rate: this.videoAdFundConfig.day31To90Rate || 0
        })
        if (response.data.success) {
          const data = response.data.data || {}
          this.videoAdFundConfig = {
            gameName: data.gameName || '',
            effectiveGameName: data.effectiveGameName || '*',
            inheritedGlobal: !!data.inheritedGlobal,
            day1To30Rate: Number(data.day1To30Rate || 0),
            day31To90Rate: Number(data.day31To90Rate || 0),
            updatedAt: data.updatedAt || ''
          }
          this.videoAdFundDialogVisible = false
          this.$message.success('广告金系数已保存')
        } else {
          this.$message.error(response.data.msg || '保存广告金系数失败')
        }
      } catch (error) {
        this.$message.error('保存广告金系数失败')
      } finally {
        this.videoAdFundConfigSaving = false
      }
    },
    currentModeName() {
      if (this.predictionMode === 'ml') {
        return 'ML预估'
      }
      if (this.predictionMode === 'curve') {
        return '曲线分组预估'
      }
      return '预估'
    },
    async loadStatus() {
      if (!this.query.calcDate) {
        return
      }
      this.statusLoading = true
      try {
        const response = await this.$axios.get(this.predictionEndpoint('/status'), {
          params: { calcDate: this.query.calcDate }
        })
        this.statusData = response.data.data || {}
      } catch (error) {
        this.statusData = {}
      } finally {
        this.statusLoading = false
      }
    },
    async loadSingleGameSnapshotStatus() {
      if (!this.query.gameName) {
        this.singleGameSnapshotStatus = {}
        return
      }
      if (!this.query.calcDate) {
        this.singleGameSnapshotStatus = {
          gameName: this.query.gameName,
          status: 'PENDING'
        }
        return
      }
      this.singleGameSnapshotLoading = true
      try {
        const response = await this.$axios.get('/mpDub/snapshot/gameStatus', {
          params: {
            calcDate: this.query.calcDate,
            gameName: this.query.gameName,
            outId: this.sourceOutId
          }
        })
        this.singleGameSnapshotStatus = response.data.data || {}
      } catch (error) {
        this.singleGameSnapshotStatus = {
          gameName: this.query.gameName,
          status: 'FAILED',
          errorMessage: '获取状态失败'
        }
      } finally {
        this.singleGameSnapshotLoading = false
      }
    },
    snapshotStatusType(status) {
      if (status === 'SUCCESS') {
        return 'success'
      }
      if (status === 'STALE_DATA' || status === 'RUNNING') {
        return 'warning'
      }
      if (status === 'FAILED' || status === 'LOGIN_EXPIRED' || status === 'EMPTY_DATA') {
        return 'danger'
      }
      return 'info'
    },
    snapshotStatusLabel(status) {
      const labels = {
        SUCCESS: '成功',
        FAILED: '失败',
        LOGIN_EXPIRED: '登录失效',
        EMPTY_DATA: '空数据',
        STALE_DATA: '数据未更新',
        RUNNING: '获取中',
        PENDING: '未获取'
      }
      return labels[status] || status || '未获取'
    },
    snapshotStatusReason(status) {
      const data = status || {}
      if (data.errorMessage) {
        return data.errorMessage
      }
      if (data.status === 'SUCCESS') {
        return '成功'
      }
      if (data.status === 'PENDING' || !data.status) {
        return '暂无获取记录'
      }
      return this.snapshotStatusLabel(data.status)
    },
    taskCalcDateTip(actionName) {
      const calcDate = this.query && this.query.calcDate ? this.query.calcDate : ''
      return actionName + '使用当前查询结果对应的计算日期' + (calcDate ? '：' + calcDate : '；请先查询出计算日期') + '，不是日期范围里的注册日期'
    },
    async submitSnapshotTask() {
      if (!this.query.calcDate) {
        this.$message.warning('请先查询出计算日期后再补发快照')
        return
      }
      this.snapshotRunLoading = true
      try {
        const response = await this.$axios.post('/mpDub/snapshot/run', {
          calcDate: this.query.calcDate,
          gameNames: this.query.gameName ? [this.query.gameName] : [],
          outId: this.sourceOutId
        })
        if (response.data.success) {
          const task = response.data.data || {}
          this.snapshotTaskId = task.taskId
          this.snapshotTaskStatus = task.status || 'PENDING'
          this.snapshotTaskMessage = task.message || ''
          this.$message.success(this.snapshotRunMessage(task))
          this.startSnapshotTaskPolling()
        } else {
          this.$message.error(response.data.msg || '提交补发快照任务失败')
        }
      } catch (error) {
        this.$message.error('提交补发快照任务失败')
      } finally {
        this.snapshotRunLoading = false
      }
    },
    snapshotRunMessage(data) {
      const prefix = data && data.reused ? '已有相同快照任务在执行' : '快照补发任务已提交'
      return prefix + '，任务ID：' + (data && data.taskId ? data.taskId : '-')
    },
    startSnapshotTaskPolling() {
      this.stopSnapshotTaskPolling()
      if (!this.snapshotTaskId) {
        return
      }
      this.pollSnapshotTask()
      this.snapshotTaskTimer = window.setInterval(() => {
        this.pollSnapshotTask()
      }, 2500)
    },
    stopSnapshotTaskPolling() {
      if (this.snapshotTaskTimer) {
        window.clearInterval(this.snapshotTaskTimer)
        this.snapshotTaskTimer = null
      }
    },
    async pollSnapshotTask() {
      if (!this.snapshotTaskId) {
        return
      }
      try {
        const response = await this.$axios.get('/mpDub/snapshot/task/' + this.snapshotTaskId)
        if (!response.data.success) {
          return
        }
        const task = response.data.data || {}
        this.snapshotTaskStatus = task.status || ''
        this.snapshotTaskMessage = task.status === 'FAILED'
          ? (task.errorMessage || task.message || '')
          : (task.message || '')
        if (this.isTaskFinished(task.status)) {
          this.stopSnapshotTaskPolling()
          if (task.status === 'SUCCESS') {
            this.$message.success(this.finishedSnapshotMessage(task.result))
            await this.loadOptions()
            await this.queryData()
            await this.loadSingleGameSnapshotStatus()
          } else if (task.status === 'FAILED') {
            this.$message.error(task.errorMessage || task.message || '快照补发任务失败')
          }
        }
      } catch (error) {
        this.snapshotTaskMessage = '任务状态查询失败'
      }
    },
    finishedSnapshotMessage(result) {
      const data = result || {}
      return '快照补发完成：Trace请求 ' + (data.fetchedTraceCount || 0) +
        '，Trace跳过 ' + (data.skippedTraceCount || 0) +
        '，复制历史 ' + (data.copiedHistoryCount || 0)
    },
    async submitPredictionTask() {
      if (this.predictionMode === 'ml') {
        await this.runMlPrediction()
        return
      }
      if (this.predictionMode === 'curve') {
        await this.runCurvePrediction()
        return
      }
      if (!this.query.calcDate) {
        this.$message.warning('请选择计算日期')
        return
      }
      this.runLoading = true
      try {
        const response = await this.$axios.post('/mpTencentPrediction/run', {
          calcDate: this.query.calcDate,
          gameNames: this.query.gameName ? [this.query.gameName] : []
        })
        if (response.data.success) {
          const data = response.data.data || {}
          this.predictionTaskId = data.taskId
          this.predictionTaskStatus = data.status || 'PENDING'
          this.predictionTaskMessage = data.message || ''
          this.$message.success(this.taskSubmitMessage(data, '生成预估'))
          this.startPredictionTaskPolling()
        } else {
          this.$message.error(response.data.msg || '提交生成预估任务失败')
        }
      } catch (error) {
        this.$message.error('提交生成预估任务失败')
      } finally {
        this.runLoading = false
      }
    },
    async runMlPrediction() {
      if (!this.query.calcDate) {
        this.$message.warning('请选择计算日期')
        return
      }
      this.runLoading = true
      try {
        const response = await this.$axios.post('/mpTencentMlPrediction/run', {
          calcDate: this.query.calcDate,
          gameNames: this.query.gameName ? [this.query.gameName] : []
        })
        if (response.data.success) {
          const data = response.data.data || {}
          this.predictionTaskId = data.taskId
          this.predictionTaskStatus = data.status || 'PENDING'
          this.predictionTaskMessage = data.message || ''
          this.$message.success(this.taskSubmitMessage(data, '生成ML预估'))
          this.startPredictionTaskPolling()
        } else {
          this.$message.error(response.data.msg || '生成ML预估失败')
        }
      } catch (error) {
        this.$message.error('生成ML预估失败，请确认 Python 依赖已安装')
      } finally {
        this.runLoading = false
      }
    },
    async runCurvePrediction() {
      if (!this.query.calcDate) {
        this.$message.warning('请选择计算日期')
        return
      }
      this.runLoading = true
      try {
        const response = await this.$axios.post(this.predictionEndpoint('/run'), {
          calcDate: this.query.calcDate,
          gameNames: this.query.gameName ? [this.query.gameName] : []
        })
        if (response.data.success) {
          const data = response.data.data || {}
          this.predictionTaskId = data.taskId
          this.predictionTaskStatus = data.status || 'PENDING'
          this.predictionTaskMessage = data.message || ''
          this.$message.success(this.taskSubmitMessage(data, '生成曲线预估'))
          this.startPredictionTaskPolling()
        } else {
          this.$message.error(response.data.msg || '生成曲线预估失败')
        }
      } catch (error) {
        this.$message.error('生成曲线预估失败，请确认 Python 依赖已安装')
      } finally {
        this.runLoading = false
      }
    },
    async submitBacktestTask() {
      if (!this.query.calcDate) {
        this.$message.warning('请选择计算日期')
        return
      }
      this.backtestLoading = true
      try {
        const response = await this.$axios.post(this.predictionEndpoint('/backtest/run'), {
          calcDate: this.query.calcDate,
          revenueType: this.showAdRevenue ? 'all' : 'monetize',
          periods: this.activeAccuracyDays,
          gameNames: (this.predictionMode === 'ml' || this.predictionMode === 'curve') && this.query.gameName ? [this.query.gameName] : []
        })
        if (response.data.success) {
          const data = response.data.data || {}
          this.backtestTaskId = data.taskId
          this.backtestTaskStatus = data.status || 'PENDING'
          this.backtestTaskMessage = data.message || ''
          this.$message.success(this.taskSubmitMessage(data, this.predictionMode === 'ml' ? '执行ML回测' : (this.predictionMode === 'curve' ? '执行曲线回测' : '执行回测')))
          this.startBacktestTaskPolling()
        } else {
          this.$message.error(response.data.msg || '提交回测任务失败')
        }
      } catch (error) {
        this.$message.error('提交回测任务失败')
      } finally {
        this.backtestLoading = false
      }
    },
    async runPrediction() {
      if (!this.query.calcDate) {
        this.$message.warning('请选择计算日期')
        return
      }
      this.runLoading = true
      try {
        const response = await this.$axios.post('/mpTencentPrediction/run', {
          calcDate: this.query.calcDate,
          gameNames: this.query.gameName ? [this.query.gameName] : []
        })
        if (response.data.success) {
          const data = response.data.data || {}
          this.$message.success('生成完成：' + (data.generatedCount || 0) + ' 条')
          await this.loadStatus()
          await this.queryData()
        } else {
          this.$message.error(response.data.msg || '生成失败')
        }
      } catch (error) {
        this.$message.error('生成失败')
      } finally {
        this.runLoading = false
      }
    },
    async runBacktest() {
      if (!this.query.calcDate) {
        this.$message.warning('请选择计算日期')
        return
      }
      this.backtestLoading = true
      try {
        const response = await this.$axios.post(this.predictionEndpoint('/backtest/run'), {
          calcDate: this.query.calcDate,
          revenueType: this.showAdRevenue ? 'all' : 'monetize',
          periods: this.activeAccuracyDays,
          gameNames: (this.predictionMode === 'ml' || this.predictionMode === 'curve') && this.query.gameName ? [this.query.gameName] : []
        })
        if (response.data.success) {
          const data = response.data.data || {}
          this.$message.success('回测完成：' + (data.sampleCount || 0) + ' 条样本')
          await this.loadBacktest()
        } else {
          this.$message.error(response.data.msg || '回测失败')
        }
      } catch (error) {
        this.$message.error('回测失败')
      } finally {
        this.backtestLoading = false
      }
    },
    async loadBacktest() {
      if (!this.query.calcDate) {
        return
      }
      this.backtestLoading = true
      try {
        const response = await this.$axios.post(this.predictionEndpoint('/backtest/find'), {
          calcDate: this.query.calcDate,
          revenueType: this.showAdRevenue ? 'all' : 'monetize',
          gameName: this.query.gameName,
          gameType: this.query.gameType,
          periodDay: this.backtestPeriod || ''
        })
        if (response.data.success) {
          const data = response.data.data || {}
          this.backtestSummary = data.summary || []
          this.backtestSamples = data.samples || []
        } else {
          this.backtestSummary = []
          this.backtestSamples = []
        }
      } catch (error) {
        this.backtestSummary = []
        this.backtestSamples = []
      } finally {
        this.backtestLoading = false
      }
    },
    taskSubmitMessage(data, actionName) {
      const prefix = data && data.reused ? '已有相同任务正在执行' : actionName + '任务已提交'
      return prefix + '，任务ID：' + (data && data.taskId ? data.taskId : '-')
    },
    taskStatusText(status) {
      if (status === 'PENDING') {
        return '等待中'
      }
      if (status === 'RUNNING') {
        return '执行中'
      }
      if (status === 'SUCCESS') {
        return '已完成'
      }
      if (status === 'FAILED') {
        return '失败'
      }
      return status || '-'
    },
    taskTagType(status) {
      if (status === 'SUCCESS') {
        return 'success'
      }
      if (status === 'FAILED') {
        return 'danger'
      }
      if (status === 'RUNNING') {
        return 'warning'
      }
      return 'info'
    },
    isTaskFinished(status) {
      return ['SUCCESS', 'FAILED', 'NOT_FOUND'].includes(status)
    },
    startPredictionTaskPolling() {
      this.stopPredictionTaskPolling()
      if (!this.predictionTaskId) {
        return
      }
      this.pollPredictionTask()
      this.predictionTaskTimer = window.setInterval(() => {
        this.pollPredictionTask()
      }, 2500)
    },
    stopPredictionTaskPolling() {
      if (this.predictionTaskTimer) {
        window.clearInterval(this.predictionTaskTimer)
        this.predictionTaskTimer = null
      }
    },
    async pollPredictionTask() {
      if (!this.predictionTaskId) {
        return
      }
      try {
        const response = await this.$axios.get(this.predictionEndpoint('/task/' + this.predictionTaskId))
        if (!response.data.success) {
          return
        }
        const task = response.data.data || {}
        const resultMessage = task.result && task.result.message ? task.result.message : ''
        this.predictionTaskStatus = task.status || ''
        this.predictionTaskMessage = task.status === 'FAILED'
          ? (task.errorMessage || task.message || '')
          : (resultMessage || task.message || '')
        if (this.isTaskFinished(task.status)) {
          this.stopPredictionTaskPolling()
          if (task.status === 'SUCCESS') {
            this.$message.success(this.finishedPredictionMessage(task.result))
            await this.loadStatus()
            await this.queryData()
          } else if (task.status === 'FAILED') {
            this.$message.error(task.errorMessage || task.message || (this.predictionMode === 'ml' ? '生成ML预估任务失败' : (this.predictionMode === 'curve' ? '生成曲线预估任务失败' : '生成预估任务失败')))
          }
        }
      } catch (error) {
        this.predictionTaskMessage = '任务状态查询失败'
      }
    },
    startBacktestTaskPolling() {
      this.stopBacktestTaskPolling()
      if (!this.backtestTaskId) {
        return
      }
      this.pollBacktestTask()
      this.backtestTaskTimer = window.setInterval(() => {
        this.pollBacktestTask()
      }, 2500)
    },
    stopBacktestTaskPolling() {
      if (this.backtestTaskTimer) {
        window.clearInterval(this.backtestTaskTimer)
        this.backtestTaskTimer = null
      }
    },
    async pollBacktestTask() {
      if (!this.backtestTaskId) {
        return
      }
      try {
        const response = await this.$axios.get(this.predictionEndpoint('/task/' + this.backtestTaskId))
        if (!response.data.success) {
          return
        }
        const task = response.data.data || {}
        this.backtestTaskStatus = task.status || ''
        this.backtestTaskMessage = task.message || ''
        if (this.isTaskFinished(task.status)) {
          this.stopBacktestTaskPolling()
          if (task.status === 'SUCCESS') {
            this.$message.success(this.finishedBacktestMessage(task.result))
            await this.loadBacktest()
          } else if (task.status === 'FAILED') {
            this.$message.error(task.errorMessage || task.message || '回测任务失败')
          }
        }
      } catch (error) {
        this.backtestTaskMessage = '任务状态查询失败'
      }
    },
    finishedPredictionMessage(result) {
      const data = result || {}
      if (this.predictionMode === 'ml') {
        if (data.scriptSkipped) {
          return 'ML预估未生成：' + (data.message || '没有可训练特征')
        }
        return 'ML预估完成：' + (data.predictionRows || 0) + ' 条'
      }
      if (this.predictionMode === 'curve') {
        return '曲线分组预估完成：' + (data.predictionRows || 0) + ' 条'
      }
      return '生成预估完成：' + (data.generatedCount || 0) + ' 条'
    },
    finishedBacktestMessage(result) {
      const data = result || {}
      return '回测完成：' + (data.sampleCount || 0) + ' 条样本'
    },
    async queryData() {
      this.loading = true
      try {
        const response = await this.$axios.post(this.predictionEndpoint('/find'), {
          calcDate: '',
          start: this.dateRange && this.dateRange.length > 0 ? this.dateRange[0] : '',
          end: this.dateRange && this.dateRange.length > 1 ? this.dateRange[1] : '',
          gameName: this.query.gameName,
          gameType: this.query.gameType,
          revenueType: this.showAdRevenue ? 'all' : 'monetize'
        })
        if (response.data.success) {
          const data = response.data.data || {}
          this.tableData = data.rows || []
          this.query.calcDate = data.calcDate || ''
          this.applySort()
          this.refreshTableLayout()
          this.loadSingleGameSnapshotStatus()
        } else {
          this.tableData = []
          this.$message.error(response.data.msg || '查询失败')
        }
      } catch (error) {
        this.tableData = []
        this.$message.error('查询失败')
      } finally {
        this.loading = false
      }
    },
    handleCalcDateChange() {
      this.dateRange = this.defaultDateRange(this.query.calcDate)
      this.loadStatus()
      this.loadSingleGameSnapshotStatus()
      this.loadBacktest()
    },
    handlePredictionModeChange() {
      if (this.backtestPeriod && !this.activeAccuracyDays.includes(this.backtestPeriod)) {
        this.backtestPeriod = ''
      }
      this.loadStatus()
      this.queryData()
    },
    handleSortChange(sortState) {
      this.sortState = sortState
      this.applySort()
    },
    applySort() {
      if (!this.sortState || !this.sortState.prop || !this.sortState.order) {
        this.refreshTableLayout()
        return
      }
      const prop = this.sortState.prop
      const direction = this.sortState.order === 'ascending' ? 1 : -1
      this.tableData = [...this.tableData].sort((left, right) => {
        const leftValue = left[prop]
        const rightValue = right[prop]
        const leftNumber = Number(leftValue)
        const rightNumber = Number(rightValue)
        if (!Number.isNaN(leftNumber) && !Number.isNaN(rightNumber)) {
          return (leftNumber - rightNumber) * direction
        }
        return String(leftValue || '').localeCompare(String(rightValue || '')) * direction
      })
      this.refreshTableLayout()
    },
    refreshTableLayout() {
      this.$nextTick(() => {
        this.tableRenderKey += 1
        this.$nextTick(() => {
          if (this.$refs.predictionTable && this.$refs.predictionTable.doLayout) {
            this.$refs.predictionTable.doLayout()
          }
        })
      })
    },
    tableRowClassName({ row }) {
      return row && row.isSummary ? 'summary-row' : ''
    },
    roiValueClass(estimated, method) {
      return estimated ? 'estimated-roi' : this.valueClass(estimated, method)
    },
    rateValueClass(estimated, method) {
      return estimated ? 'estimated-rate' : this.valueClass(estimated, method)
    },
    valueClass(estimated, method) {
      if (!estimated) {
        return ''
      }
      if (method === 'ML_ENSEMBLE' || method === 'CATBOOST' || method === 'LIGHTGBM') {
        return 'estimated-ml'
      }
      if (method === 'LOG_FIT' || method === 'INTERPOLATION') {
        return 'estimated-curve-fit'
      }
      if (method === 'GAME_GROUP_TREND' || method === 'GAME_GROUP_TREND_MIXED' || method === 'TYPE_GROUP_TREND' || method === 'TYPE_GROUP_TREND_MIXED' || method === 'GLOBAL_GROUP_TREND' || method === 'GLOBAL_GROUP_TREND_MIXED') {
        return 'estimated-curve-trend'
      }
      if (method === 'GAME' || method === 'GAME_GROUP' || method === 'GAME_GROUP_BLEND') {
        return 'estimated-game'
      }
      if (method === 'GAME_BLEND') {
        return 'estimated-game'
      }
      if (method === 'GAME_TYPE' || method === 'GAME_TYPE_GROUP' || method === 'GAME_TYPE_GROUP_BLEND') {
        return 'estimated-game-type'
      }
      if (method === 'GAME_TYPE_BLEND') {
        return 'estimated-game-type'
      }
      if (method === 'GLOBAL' || method === 'GLOBAL_GROUP' || method === 'GLOBAL_GROUP_BLEND') {
        return 'estimated-global'
      }
      if (method === 'GLOBAL_BLEND' || method === 'REAL_BASE_BLEND') {
        return 'estimated-global'
      }
      return 'estimated-value'
    },
    methodTitle(method) {
      if (method === 'ML_ENSEMBLE') {
        return 'CatBoost 和 LightGBM 按回测误差加权融合后的机器学习预估'
      }
      if (method === 'CATBOOST') {
        return 'CatBoost 表格模型预估'
      }
      if (method === 'LIGHTGBM') {
        return 'LightGBM 表格模型预估'
      }
      if (method === 'GAME_GROUP_TREND') {
        return '按同游戏、同曲线分组的 15→30→45→60→75→90 趋势修复'
      }
      if (method === 'GAME_GROUP_TREND_MIXED') {
        return '低首日高倍率组样本不足时，回退到同游戏混合模式组趋势'
      }
      if (method === 'TYPE_GROUP_TREND') {
        return '按同品类、同曲线分组的趋势修复'
      }
      if (method === 'TYPE_GROUP_TREND_MIXED') {
        return '低首日高倍率组样本不足时，回退到同品类混合模式组趋势'
      }
      if (method === 'GLOBAL_GROUP_TREND') {
        return '按腾讯广告全局同曲线分组趋势修复'
      }
      if (method === 'GLOBAL_GROUP_TREND_MIXED') {
        return '低首日高倍率组样本不足时，回退到全局混合模式组趋势'
      }
      if (method === 'LOG_FIT') {
        return '高首日低倍率组使用对数曲线拟合补齐缺失周期'
      }
      if (method === 'INTERPOLATION') {
        return '趋势样本不足时使用已有 ROI 点做插值补齐'
      }
      if (method === 'CALIBRATION') {
        return '根据曲线分组回测误差生成校准系数，只修正预估值，不改真实 Trace'
      }
      if (method === 'GAME_GROUP') {
        return '按同游戏内训练分组预估，同一个游戏会区分高首日低倍率、低首日高倍率等样本'
      }
      if (method === 'GAME_GROUP_BLEND') {
        return '使用多个真实已出周期，按同游戏内训练分组融合预估'
      }
      if (method === 'GAME_BLEND') {
        return '使用多个真实已出周期，按同游戏历史倍率融合预估'
      }
      if (method === 'GAME_TYPE_GROUP') {
        return '按同品类训练分组预估'
      }
      if (method === 'GAME_TYPE_GROUP_BLEND') {
        return '使用多个真实已出周期，按同品类训练分组融合预估'
      }
      if (method === 'GAME_TYPE_BLEND') {
        return '使用多个真实已出周期，按同品类历史倍率融合预估'
      }
      if (method === 'GLOBAL_GROUP') {
        return '按腾讯广告全局训练分组预估'
      }
      if (method === 'GLOBAL_GROUP_BLEND') {
        return '使用多个真实已出周期，按全局训练分组融合预估'
      }
      if (method === 'GLOBAL_BLEND') {
        return '使用多个真实已出周期，按全局历史倍率融合预估'
      }
      if (method === 'REAL_BASE_BLEND') {
        return '使用多个真实已出周期融合预估，避免预测值继续作为下一步基准'
      }
      if (method === 'GAME') {
        return '按同游戏整体历史倍率预估'
      }
      if (method === 'GAME_TYPE') {
        return '按同品类整体历史倍率预估'
      }
      if (method === 'GLOBAL') {
        return '按腾讯广告全局整体倍率预估'
      }
      return ''
    },
    cellTitle(row, day) {
      if (!row || !day) {
        return ''
      }
      const method = row[`roi${day}Method`] || (day === row.bestPeriod ? row.bestMethod : '')
      const titles = []
      const methodText = this.methodTitle(method)
      if (methodText) {
        titles.push(methodText)
      }
      const calibration = row[`roi${day}Calibration`] || (day === row.bestPeriod ? row.bestCalibration : '')
      const calibrationText = this.calibrationText(calibration)
      if (calibrationText) {
        titles.push(calibrationText)
      }
      return titles.join('\n')
    },
    calibrationText(value) {
      if (!value) {
        return ''
      }
      const parts = String(value).split('|')
      const factor = parts[0] || '-'
      const risk = this.riskLabel(parts[1])
      const calcDate = parts[2] || ''
      return `回测校准系数 ${factor}${risk ? '，' + risk : ''}${calcDate ? '，来源 ' + calcDate : ''}`
    },
    calibrationSummary(row) {
      if (!row || !row.calibrationDetail) {
        return ''
      }
      return String(row.calibrationDetail)
        .split(',')
        .map(item => {
          const parts = item.split(':')
          return parts.length === 2 ? parts[0] + ' ' + this.calibrationText(parts[1]) : item
        })
        .join('\n')
    },
    formatPredictionInterval(row) {
      if (!row) {
        return '-'
      }
      if (this.hasValue(row.predictionLow) && this.hasValue(row.predictionHigh)) {
        return this.formatPercent(row.predictionLow) + ' - ' + this.formatPercent(row.predictionHigh)
      }
      if (row.bestInterval) {
        const parts = String(row.bestInterval).split('|')
        if (parts.length === 2) {
          return this.formatPercent(parts[0]) + ' - ' + this.formatPercent(parts[1])
        }
      }
      return '-'
    },
    riskLabel(riskLevel) {
      if (riskLevel === 'HIGH') {
        return '高风险'
      }
      if (riskLevel === 'MEDIUM') {
        return '中风险'
      }
      if (riskLevel === 'LOW') {
        return '低风险'
      }
      return ''
    },
    riskTagType(riskLevel) {
      if (riskLevel === 'HIGH') {
        return 'danger'
      }
      if (riskLevel === 'MEDIUM') {
        return 'warning'
      }
      if (riskLevel === 'LOW') {
        return 'success'
      }
      return 'info'
    },
    methodTags(row) {
      const levels = String(row.modelLevel || '')
        .split(',')
        .map(item => item.trim())
        .filter(item => item && item !== 'TRACE')
      const uniqueLevels = [...new Set(levels)]
      return uniqueLevels.map(method => ({
        method,
        label: this.methodShortLabel(method)
      })).filter(item => item.label)
    },
    methodShortLabel(method) {
      if (method === 'ML_ENSEMBLE') {
        return 'ML融合'
      }
      if (method === 'CATBOOST') {
        return 'CatBoost'
      }
      if (method === 'LIGHTGBM') {
        return 'LightGBM'
      }
      if (method === 'GAME_GROUP_TREND') {
        return '同游戏分组趋势'
      }
      if (method === 'GAME_GROUP_TREND_MIXED') {
        return '同游戏混合组趋势'
      }
      if (method === 'TYPE_GROUP_TREND') {
        return '同品类分组趋势'
      }
      if (method === 'TYPE_GROUP_TREND_MIXED') {
        return '同品类混合组趋势'
      }
      if (method === 'GLOBAL_GROUP_TREND') {
        return '全局分组趋势'
      }
      if (method === 'GLOBAL_GROUP_TREND_MIXED') {
        return '全局混合组趋势'
      }
      if (method === 'LOG_FIT') {
        return '对数拟合'
      }
      if (method === 'INTERPOLATION') {
        return '插值'
      }
      if (method === 'CALIBRATION') {
        return '回测校准'
      }
      if (method === 'GAME_GROUP_BLEND') {
        return '同游戏分组融合'
      }
      if (method === 'GAME_GROUP') {
        return '同游戏分组'
      }
      if (method === 'GAME_BLEND') {
        return '同游戏融合'
      }
      if (method === 'GAME_TYPE_GROUP_BLEND') {
        return '同品类分组融合'
      }
      if (method === 'GAME_TYPE_GROUP') {
        return '同品类分组'
      }
      if (method === 'GAME_TYPE_BLEND') {
        return '同品类融合'
      }
      if (method === 'GLOBAL_GROUP_BLEND') {
        return '全局分组融合'
      }
      if (method === 'GLOBAL_GROUP') {
        return '全局分组'
      }
      if (method === 'GLOBAL_BLEND') {
        return '全局融合'
      }
      if (method === 'REAL_BASE_BLEND') {
        return '真实基准融合'
      }
      if (method === 'GAME') {
        return '同游戏'
      }
      if (method === 'GAME_TYPE') {
        return '同品类'
      }
      if (method === 'GLOBAL') {
        return '全局'
      }
      return ''
    },
    methodTagType(method) {
      if (method === 'ML_ENSEMBLE') {
        return 'success'
      }
      if (method === 'CATBOOST' || method === 'LIGHTGBM') {
        return ''
      }
      if (method === 'GAME_GROUP_TREND' || method === 'GAME_GROUP_TREND_MIXED') {
        return 'success'
      }
      if (method === 'TYPE_GROUP_TREND' || method === 'TYPE_GROUP_TREND_MIXED') {
        return 'warning'
      }
      if (method === 'GLOBAL_GROUP_TREND' || method === 'GLOBAL_GROUP_TREND_MIXED' || method === 'LOG_FIT' || method === 'INTERPOLATION') {
        return 'info'
      }
      if (method === 'CALIBRATION') {
        return 'danger'
      }
      if (method === 'GAME_BLEND' || method === 'GAME_GROUP' || method === 'GAME_GROUP_BLEND') {
        return 'success'
      }
      if (method === 'GAME_TYPE_BLEND' || method === 'GAME_TYPE_GROUP' || method === 'GAME_TYPE_GROUP_BLEND') {
        return 'warning'
      }
      if (method === 'GLOBAL_BLEND' || method === 'GLOBAL_GROUP' || method === 'GLOBAL_GROUP_BLEND' || method === 'REAL_BASE_BLEND') {
        return 'info'
      }
      if (method === 'GAME') {
        return 'success'
      }
      if (method === 'GAME_TYPE') {
        return 'warning'
      }
      if (method === 'GLOBAL') {
        return 'info'
      }
      return ''
    },
    scopeLabel(scopeType) {
      if (scopeType === 'GLOBAL') {
        return '全局'
      }
      if (scopeType === 'GAME') {
        return '游戏'
      }
      if (scopeType === 'GAME_TYPE') {
        return '品类'
      }
      if (scopeType === 'MODEL') {
        return '预估方式'
      }
      if (scopeType === 'GAME_GROUP') {
        return '游戏+分组'
      }
      if (scopeType === 'GAME_TYPE_GROUP') {
        return '品类+分组'
      }
      return scopeType || '-'
    },
    biasClass(value) {
      const numberValue = Number(value)
      if (Number.isNaN(numberValue) || numberValue === 0) {
        return 'profit-zero'
      }
      return numberValue > 0 ? 'profit-positive' : 'profit-negative'
    },
    groupTagType(label) {
      if (label === 'ML未分组') {
        return 'info'
      }
      if (label === '低首日ROI-高倍率组') {
        return 'success'
      }
      if (label === '高首日ROI-低倍率组') {
        return 'warning'
      }
      return 'info'
    },
    displayGroupLabel(row) {
      if (this.predictionMode === 'ml') {
        return row && row.groupLabel ? row.groupLabel : 'ML未分组'
      }
      return row && row.groupLabel ? row.groupLabel : '样本不足组'
    },
    profitClass(value) {
      const numberValue = Number(value)
      if (Number.isNaN(numberValue) || numberValue === 0) {
        return 'profit-zero'
      }
      return numberValue > 0 ? 'profit-positive' : 'profit-negative'
    },
    buildSummaryRow(rows) {
      const summary = {
        isSummary: true,
        regDs: '合计',
        gameType: '合计',
        gameName: this.summaryGameName(rows),
        outId: '',
        revenueType: this.showAdRevenue ? 'all' : 'monetize',
        adCost: this.sumCost(rows),
        regUv: rows.reduce((sum, row) => sum + this.toNumber(row.regUv), 0),
        estimatedProfit: this.sumEstimatedProfit(rows),
        actualMaxPeriod: '',
        predictedPeriod: '',
        predictionLow: null,
        predictionHigh: null,
        repairedColumns: '',
        predictionDetail: '',
        intervalDetail: '',
        riskLevel: '',
        modelLevel: '',
        modelVersion: '',
        groupLabel: '合计',
        repairBasis: '',
        repairDetails: '',
        calibrated: false,
        bestPeriod: '',
        bestEstimated: false,
        bestMethod: ''
      }
      const bestTotal = this.weightedRoiAndRate(rows, 'bestRoi')
      summary.bestRoi = bestTotal.roi
      summary.bestRate = bestTotal.rate
      this.displayRoiDays.forEach(day => {
        const total = this.weightedRoiAndRate(rows, 'roi' + day)
        summary['roi' + day] = total.roi
        summary['rate' + day] = total.rate
        summary['roi' + day + 'Estimated'] = false
        summary['roi' + day + 'Method'] = ''
      })
      return summary
    },
    summaryGameName(rows) {
      const gameNames = [...new Set((rows || []).map(row => row.gameName).filter(Boolean))]
      if (gameNames.length === 1) {
        return gameNames[0] + ' 合计'
      }
      return '多游戏合计'
    },
    summaryMethod({ columns, data }) {
      const rows = Array.isArray(data) ? data : []
      return columns.map((column, index) => {
        const prop = column.property
        if (index === 0) {
          return '合计'
        }
        if (prop === 'adCost') {
          return this.formatMoney(this.sumCost(rows))
        }
        if (prop === 'estimatedProfit') {
          return this.formatMoney(this.sumEstimatedProfit(rows))
        }
        if (prop === 'bestRoi') {
          const total = this.weightedRoiAndRate(rows, 'bestRoi')
          return this.formatMetricSummary(total.roi, total.rate)
        }
        const roiMatch = prop && String(prop).match(/^roi(\d+)$/)
        if (roiMatch) {
          const total = this.weightedRoiAndRate(rows, prop)
          return this.formatMetricSummary(total.roi, total.rate)
        }
        return ''
      })
    },
    sumCost(rows) {
      return rows.reduce((sum, row) => sum + this.toNumber(row.adCost), 0)
    },
    sumEstimatedProfit(rows) {
      return rows.reduce((sum, row) => {
        const cost = this.toNumber(row.adCost)
        const roi = this.validNumber(row.bestRoi)
        if (cost <= 0 || roi === null) {
          return sum
        }
        return sum + cost * roi - cost
      }, 0)
    },
    weightedRoiAndRate(rows, roiProp) {
      let income = 0
      let cost = 0
      let baseIncome = 0
      rows.forEach(row => {
        const rowCost = this.toNumber(row.adCost)
        const roi = this.validNumber(row[roiProp])
        const roi1 = this.validNumber(row.roi1)
        if (rowCost <= 0 || roi === null) {
          return
        }
        income += rowCost * roi
        cost += rowCost
        if (roi1 !== null) {
          baseIncome += rowCost * roi1
        }
      })
      return {
        roi: cost > 0 ? income / cost : null,
        rate: baseIncome > 0 ? income / baseIncome : null
      }
    },
    formatMetricSummary(roi, rate) {
      const parts = []
      if (this.checkedMetrics.includes('ROI')) {
        parts.push(this.formatPercent(roi))
      }
      if (this.checkedMetrics.includes('鍊嶇巼')) {
        parts.push(this.formatRate(rate))
      }
      return parts.filter(item => item !== '-').length > 0 ? parts.join(' | ') : '-'
    },
    validNumber(value) {
      if (!this.hasValue(value)) {
        return null
      }
      const numberValue = Number(value)
      return Number.isNaN(numberValue) ? null : numberValue
    },
    toNumber(value) {
      const numberValue = Number(value)
      return Number.isNaN(numberValue) ? 0 : numberValue
    },
    formatMoney(value) {
      if (!this.hasValue(value)) {
        return '-'
      }
      const numberValue = Number(value)
      return Number.isNaN(numberValue) ? '-' : numberValue.toFixed(2)
    },
    formatPercent(value) {
      if (!this.hasValue(value)) {
        return '-'
      }
      const numberValue = Number(value)
      return Number.isNaN(numberValue) ? '-' : (numberValue * 100).toFixed(2) + '%'
    },
    formatRate(value) {
      if (!this.hasValue(value)) {
        return '-'
      }
      const numberValue = Number(value)
      return Number.isNaN(numberValue) ? '-' : numberValue.toFixed(2)
    },
    hasValue(value) {
      if (value === null || value === undefined || value === '') {
        return false
      }
      const numberValue = Number(value)
      return !Number.isNaN(numberValue) && numberValue !== 0
    },
    formatDate(value) {
      if (!value) {
        return '-'
      }
      const text = String(value)
      if (text.length === 8) {
        return text.substring(0, 4) + '-' + text.substring(4, 6) + '-' + text.substring(6, 8)
      }
      return text
    },
    defaultCalcDate() {
      return this.formatDateValue(new Date())
    },
    defaultDateRange(calcDate) {
      const base = this.parseDate(calcDate)
      const end = this.addDays(base, -1)
      const start = this.addDays(end, -6)
      return [this.formatDayValue(start), this.formatDayValue(end)]
    },
    parseDate(value) {
      if (!value) {
        return new Date()
      }
      const parts = value.split('-').map(item => Number(item))
      return new Date(parts[0], parts[1] - 1, parts[2])
    },
    addDays(date, days) {
      const next = new Date(date.getTime())
      next.setDate(next.getDate() + days)
      return next
    },
    formatDateValue(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return year + '-' + month + '-' + day
    },
    formatDayValue(date) {
      return this.formatDateValue(date).replace(/-/g, '')
    }
  }
}
</script>

<style scoped>
.mp-tencent-prediction-page {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  background: #f5f7fb;
  padding: 10px 14px 16px;
  color: #303133;
  box-sizing: border-box;
}

.page-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.page-toolbar h2 {
  margin: 0;
  font-size: 20px;
  line-height: 26px;
  color: #202226;
}

.page-toolbar span {
  display: block;
  margin-top: 2px;
  color: #8c939d;
  font-size: 12px;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.snapshot-status-button {
  padding: 7px;
}

.snapshot-status-panel {
  min-height: 120px;
  color: #303133;
}

.snapshot-status-title {
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 600;
}

.snapshot-status-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 26px;
  font-size: 12px;
}

.snapshot-status-row span,
.snapshot-status-reason span {
  color: #8c939d;
}

.snapshot-status-row strong {
  font-weight: 400;
  color: #303133;
}

.snapshot-status-reason {
  margin-top: 6px;
  font-size: 12px;
}

.snapshot-status-reason p {
  margin: 4px 0 0;
  line-height: 1.5;
  color: #606266;
  word-break: break-all;
}

.query-section,
.status-section,
.table-section {
  background: #fff;
  border: 1px solid #e6ebf2;
}

.query-section {
  padding: 10px 12px;
  margin-bottom: 8px;
}

.query-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.field-label {
  font-size: 13px;
  color: #303133;
  white-space: nowrap;
}

.metric-checks {
  display: flex;
  align-items: center;
  height: 28px;
  margin-left: 4px;
}

.inline-task-status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 360px;
  color: #606266;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.video-ad-fund-help {
  padding-left: 2px;
  color: #8c939d;
  font-size: 12px;
  line-height: 1.6;
}

.video-ad-fund-inherit {
  margin-left: 8px;
  color: #8c939d;
  font-size: 12px;
}

.column-popover {
  display: flex;
  flex-direction: column;
}

.column-popover /deep/ .el-checkbox {
  display: block;
  margin: 0 0 8px 0;
}

.status-section {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 8px 12px;
  margin-bottom: 8px;
  font-size: 13px;
}

.status-title {
  font-weight: 600;
}

.status-extra {
  color: #606266;
}

.task-extra {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #606266;
}

.backtest-section {
  order: 2;
  background: #fff;
  border: 1px solid #e6ebf2;
  padding: 10px 12px;
  margin-bottom: 8px;
}

.backtest-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.backtest-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.backtest-subtitle {
  margin-left: 8px;
  font-size: 12px;
  color: #8c939d;
}

.backtest-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.backtest-table {
  margin-bottom: 4px;
}

.backtest-sample-table {
  margin-top: 8px;
}

.backtest-table-note {
  margin: 4px 0 8px;
  color: #8c939d;
  font-size: 12px;
  line-height: 18px;
}

.table-section {
  order: 1;
  padding: 8px 8px 10px;
  margin-bottom: 8px;
}

.table-count {
  margin-top: 8px;
  text-align: right;
  color: #666;
  font-size: 13px;
}

.logic-content {
  color: #303133;
}

.logic-block {
  padding: 0 0 12px;
  margin-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
}

.logic-block:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: 0;
}

.logic-block h3 {
  margin: 0 0 8px;
  font-size: 15px;
  color: #202226;
}

.logic-block p {
  margin: 4px 0;
  line-height: 1.7;
  font-size: 13px;
  color: #606266;
}

.estimated-value {
  color: #3a8ee6;
}

.estimated-game {
  color: #2aa84a;
}

.estimated-game-type {
  color: #e6a23c;
}

.estimated-global {
  color: #3a8ee6;
}

.estimated-ml {
  color: #3a8ee6;
}

.estimated-curve-fit {
  color: #8b5cf6;
}

.estimated-curve-trend {
  color: #0f9f8f;
}

.estimated-roi {
  color: #3a8ee6;
}

.estimated-rate {
  color: #d65055;
}

.estimated-fit {
  color: #d65055;
}

.method-tag {
  margin-right: 4px;
}

.roi-inline {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  min-width: 112px;
}

.profit-positive {
  color: #d65055;
}

.profit-negative {
  color: #28a745;
}

.profit-zero {
  color: #606266;
}

.prediction-table /deep/ .summary-row > td {
  position: sticky;
  top: 0;
  z-index: 3;
  background: #f8fbff !important;
  font-weight: 600;
}

.prediction-table /deep/ .el-table__fixed .summary-row > td,
.prediction-table /deep/ .el-table__fixed-right .summary-row > td {
  z-index: 5;
}

.prediction-table /deep/ .el-table__header th,
.prediction-table /deep/ .el-table__body td {
  background: #fff;
}

.prediction-table /deep/ .el-table__body tr.current-row > td,
.prediction-table /deep/ .el-table__fixed .el-table__body tr.current-row > td,
.prediction-table /deep/ .el-table__fixed-right .el-table__body tr.current-row > td {
  background: #eaf4ff !important;
}

.prediction-table /deep/ .el-table__body tr.hover-row > td,
.prediction-table /deep/ .el-table__fixed .el-table__body tr.hover-row > td,
.prediction-table /deep/ .el-table__fixed-right .el-table__body tr.hover-row > td {
  background: #f5f9ff !important;
}

.prediction-table /deep/ .el-table__fixed,
.prediction-table /deep/ .el-table__fixed-right {
  background: #fff;
  z-index: 4;
}

.prediction-table /deep/ .el-table__fixed th,
.prediction-table /deep/ .el-table__fixed td,
.prediction-table /deep/ .el-table__fixed-right th,
.prediction-table /deep/ .el-table__fixed-right td {
  background: #fff;
}

.prediction-table /deep/ .el-table__fixed::before,
.prediction-table /deep/ .el-table__fixed-right::before {
  background: #e6ebf2;
}

.prediction-table /deep/ .el-table__fixed .cell,
.prediction-table /deep/ .el-table__fixed-right .cell {
  overflow: hidden;
  text-overflow: ellipsis;
}

/deep/ .el-table {
  font-size: 12px;
}

/deep/ .el-table .cell {
  white-space: nowrap;
}

.prediction-table /deep/ .el-table__fixed,
.prediction-table /deep/ .el-table__fixed-right {
  box-shadow: 0 0 6px rgba(0, 0, 0, 0.08);
}
</style>
